import React from "react";
import { motion } from "framer-motion";
import {
  Wrench, Snowflake, BatteryCharging, Disc3, PaintBucket,
  Sparkles, Droplets, ScanLine, Lightbulb, Cog, Settings2, ShieldCheck,
} from "lucide-react";
import { classes } from "./tokens";

/**
 * Services Grid — gomechanic-style 12-card service category showcase.
 * Only "General Service" is active in v1; remaining 11 show "Coming Soon"
 * to set up the eventual product roadmap visually without making the page
 * feel half-built.
 */
const services = [
  { key: "general",      label: "General Service",     icon: Wrench,          tone: "orange",  active: true,  badge: "Available Now" },
  { key: "ac",           label: "AC Service & Repair", icon: Snowflake,       tone: "blue" },
  { key: "battery",      label: "Battery Replacement", icon: BatteryCharging, tone: "green" },
  { key: "tyres",        label: "Tyres & Wheel Care",  icon: Disc3,           tone: "slate" },
  { key: "denting",      label: "Denting & Painting",  icon: PaintBucket,     tone: "red" },
  { key: "detailing",    label: "Detailing",           icon: Sparkles,        tone: "purple" },
  { key: "spa",          label: "Car Spa & Cleaning",  icon: Droplets,        tone: "cyan" },
  { key: "inspection",   label: "Pre-Buy Inspection",  icon: ScanLine,        tone: "amber",   badge: "Live" },
  { key: "windshields",  label: "Windshields & Lights",icon: Lightbulb,       tone: "yellow" },
  { key: "suspension",   label: "Suspension & Fitments",icon: Cog,            tone: "slate" },
  { key: "clutch",       label: "Clutch & Body Parts", icon: Settings2,       tone: "blue" },
  { key: "insurance",    label: "Insurance Claims",    icon: ShieldCheck,     tone: "green" },
];

const TONE = {
  orange: { bg: "bg-blue-50",  ring: "ring-blue-200", icon: "text-blue-600",  iconBg: "bg-white" },
  blue:   { bg: "bg-blue-50",    ring: "ring-blue-200",   icon: "text-blue-600",    iconBg: "bg-white" },
  green:  { bg: "bg-green-50",   ring: "ring-green-200",  icon: "text-green-600",   iconBg: "bg-white" },
  slate:  { bg: "bg-slate-100",  ring: "ring-slate-200",  icon: "text-slate-700",   iconBg: "bg-white" },
  red:    { bg: "bg-red-50",     ring: "ring-red-200",    icon: "text-red-600",     iconBg: "bg-white" },
  purple: { bg: "bg-purple-50",  ring: "ring-purple-200", icon: "text-purple-600",  iconBg: "bg-white" },
  cyan:   { bg: "bg-cyan-50",    ring: "ring-cyan-200",   icon: "text-cyan-600",    iconBg: "bg-white" },
  amber:  { bg: "bg-amber-50",   ring: "ring-amber-200",  icon: "text-amber-700",   iconBg: "bg-white" },
  yellow: { bg: "bg-yellow-50",  ring: "ring-yellow-200", icon: "text-yellow-700",  iconBg: "bg-white" },
};

const CarServiceServicesGrid = () => (
  <section
    className="py-16 lg:py-24 bg-white"
    data-testid="services-grid-section"
  >
    <div className={classes.container}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <span className={classes.overline}>Our Services</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-slate-900 leading-tight mt-2">
            All your car needs.<br className="sm:hidden" />
            <span className="text-slate-500"> One trusted partner.</span>
          </h2>
        </div>
        <p className="text-sm text-slate-500 max-w-md">
          Starting with General Service today.
          More categories rolling out — pre-purchase inspection is already live on our main site.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {services.map((s, i) => {
          const tone = TONE[s.tone] || TONE.slate;
          const isActive = !!s.active;
          const isLive = s.badge === "Live";
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.35, delay: (i % 4) * 0.04 }}
              className={`relative rounded-2xl p-5 transition-all ${
                isActive
                  ? `${tone.bg} ring-2 ring-blue-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]`
                  : isLive
                    ? `${tone.bg} ring-1 ${tone.ring} hover:-translate-y-0.5`
                    : "bg-slate-50 ring-1 ring-slate-100 opacity-70 hover:opacity-100 transition-opacity"
              }`}
              data-testid={`service-card-${s.key}`}
            >
              {/* Top-right corner badge */}
              {isActive && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[9px] font-bold uppercase tracking-[0.18em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Live
                </span>
              )}
              {!isActive && !isLive && (
                <span className="absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold uppercase tracking-[0.18em]">
                  Soon
                </span>
              )}
              {isLive && !isActive && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[9px] font-bold uppercase tracking-[0.18em]">
                  Live
                </span>
              )}

              <div className={`w-12 h-12 rounded-xl ${tone.iconBg} ${isActive ? "" : "opacity-90"} shadow-sm inline-flex items-center justify-center mb-4`}>
                <s.icon className={`h-6 w-6 ${isActive || isLive ? tone.icon : "text-slate-400"}`} strokeWidth={2.2} />
              </div>
              <div className={`text-sm sm:text-base font-extrabold leading-snug ${isActive || isLive ? "text-slate-900" : "text-slate-500"}`}>
                {s.label}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

export default CarServiceServicesGrid;
